import { useCallback, useRef, useState } from 'react'
import { scanNotice } from '@/components/pbo/notice'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { useSaveWorkRegistrations } from '@/pages/return/rgr11V2/queries/useReturnWorkQueries'
import { buildSaveRequest, toYyyyMmDdHHmmss, WorkTableRow } from '@/pages/return/rgr11V2/queries/types'
import { validateSaveRequest } from '@/pages/return/rgr11V2/schemas/rgr11Schema'
import { AttachmentUploadTask, useAttachmentUpload } from '@/pages/return/rgr11V2/hooks/useAttachmentUpload'
import { MESSAGES } from '@/pages/return/rgr11V2/constants'
import { LABEL_PRINTER_CONFIG, printHtml } from '@/pages/return/rgr11V2/print/qzTrayClient'
import { buildLabelHtml, LabelPrintData } from '@/pages/return/rgr11V2/print/RgrLabelTemplate'

export interface SaveHeadInput {
  shipperCode: string
  returnInvoiceNumber: string
  originalInvoiceNumber?: string
  receiverAddress?: string
  receiverPhone?: string
  receiverName?: string
  jobCommentForInvoice?: string
  isSendToDoosonERP?: boolean
}

export interface SaveOutcome {
  /** 저장에서 제외된 중복 반품번호 행 — 표를 통째로 비우지 않고 이 행만 남겨 사용자가 알아볼 수 있게 한다(6절 5단계). */
  duplicateRows: WorkTableRow[]
  /** 첨부 업로드가 실패(또는 targetIds 미수신)했으면 true. 옛 구현 `Rgr11Page` 만 읽는다 — 새 화면은 실패분을 대기 중 첨부(`pendingAttachments`)로 따로 들고 화면에서는 늘 뺀다. targetIds 미수신이면 붙일 행이 없어 재시도 대상도 아니다. */
  attachmentFailed: boolean
}

export interface RunSaveParams {
  head: SaveHeadInput
  rows: WorkTableRow[]
  /** 스캔 시작 시각 — 최초 HEAD 조회 성공 시점을 호출부가 들고 있다가 넘긴다(1.0 `REC_START_DATE`). */
  receiptStartedAt: Date
  attachmentTasks: AttachmentUploadTask[]
  /** 저장 직후 자동 인쇄용 라벨 데이터 — 없으면 인쇄를 건너뛴다. */
  labelPrintData: LabelPrintData | null
  onSuccess: (outcome: SaveOutcome) => void
}

interface PendingAttachment {
  shipper: string
  tasks: AttachmentUploadTask[]
  targetIds: number[]
}

/**
 * 저장 오케스트레이션(6절 5단계) — zod 검증 → 저장 → `duplicates` 안내 → 첨부 업로드 → 라벨 인쇄 → 리셋.
 *
 * 저장 자체는 `duplicates` 가 있어도 성공으로 본다(피드백 5, RGR06 과 같은 규칙) — 다만 어느 행이
 * 빠졌는지 화면에서 사라지면 안 되므로 그 행만 표에 남긴다(호출부 `onSuccess`). 저장 직후 자동
 * 인쇄는 qz-tray 미설치 환경에서 실패해도 삼켜 저장 흐름을 막지 않는다(8.1-10) — 첨부 업로드 실패는
 * 삼키지 않고 안내하며, 재시도할 수 있게 원본을 지우지 않고 `retryAttachmentUpload` 로 다시 시도한다
 * (3.5, PR #691 리뷰 코멘트 — 재시도 버튼 없이 종료하지 않는다).
 */
export const useRgr11Save = () => {
  const saveMutation = useSaveWorkRegistrations()
  const attachmentMutation = useAttachmentUpload()
  const { mutateAsync: save } = saveMutation
  const { mutateAsync: uploadAttachment } = attachmentMutation
  const [isSaving, setIsSaving] = useState(false)
  /** state 는 같은 tick 에서 갱신되지 않아 재진입 방어가 안 된다(SAVE 바코드 치트 연속 스캔 가드). */
  const isSavingRef = useRef(false)
  /**
   * 대기 중 첨부 — 저장은 됐는데 올리지 못한 파일을 저장된 행(targetIds)별로 들고 있다. 화면의 촬영·녹화 상태와 떼어 둬야
   * 재시도 없이 다음 건을 저장해도 이 파일이 다음 건 행에 붙지 않는다(LOGISPRD-14589). 여러 건이 쌓이면 재시도가 모두 올린다.
   */
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])

  /** 올리고, 실패한 파일만 남긴 대기 건을 돌려준다(전부 올렸으면 null) — 성공한 파일을 재시도에서 다시 올리면 같은 행에 두 번 붙는다. */
  const uploadAttachments = useCallback(
    async (input: PendingAttachment): Promise<PendingAttachment | null> => {
      try {
        const uploadResult = await uploadAttachment(input)
        return uploadResult.failed.length > 0 ? { ...input, tasks: uploadResult.failed.map((f) => f.task) } : null
      } catch {
        return input
      }
    },
    [uploadAttachment],
  )

  /** 재시도 중 [첨부 재시도]를 또 누르면 같은 파일이 같은 행에 두 번 붙는다 — 버튼에 바쁨 표시가 없어 여기서 막는다. */
  const isRetryingRef = useRef(false)
  /**
   * 첨부만 다시 시도한다 — 저장은 이미 끝났으므로 재저장하지 않는다. 재시도 중 새로 쌓인 대기 건은 건드리지 않는다.
   * onDone — 모두 올라갔을 때 부른다(옛 구현 `Rgr11Page` 만 쓴다. 새 화면은 저장 때 화면 상태를 이미 비웠다).
   */
  const retryAttachmentUpload = useCallback(
    async (onDone?: () => void) => {
      const retrying = pendingAttachments
      if (retrying.length === 0 || isRetryingRef.current) return
      isRetryingRef.current = true
      try {
        const left = (await Promise.all(retrying.map((p) => uploadAttachments(p)))).filter((p): p is PendingAttachment => p != null)
        setPendingAttachments((prev) => [...prev.filter((p) => !retrying.includes(p)), ...left])
        if (left.length > 0) scanNotice.failure(MESSAGES.attachmentUploadFailed)
        else onDone?.()
      } finally {
        isRetryingRef.current = false
      }
    },
    [pendingAttachments, uploadAttachments],
  )

  /** 저장 뒤 첨부 업로드 — 실패면 안내 문구, 성공·첨부 없음이면 null. 안내는 저장 흐름이 다른 실패와 모아 한 장으로 띄운다. */
  const uploadAfterSave = useCallback(
    async (shipper: string, tasks: RunSaveParams['attachmentTasks'], targetIds: PendingAttachment['targetIds']): Promise<string | null> => {
      if (tasks.length === 0) return null
      if (targetIds.length === 0) return MESSAGES.attachmentTargetMissing
      const left = await uploadAttachments({ shipper, tasks, targetIds })
      if (!left) return null
      setPendingAttachments((prev) => [...prev, left])
      return MESSAGES.attachmentUploadFailed
    },
    [uploadAttachments],
  )

  const runSave = useCallback(
    async (params: RunSaveParams) => {
      const { head, rows, receiptStartedAt, attachmentTasks, labelPrintData, onSuccess } = params

      if (isSavingRef.current) return
      if (!head.returnInvoiceNumber) {
        scanNotice.invalid(MESSAGES.invoiceRequired)
        return
      }

      const request = buildSaveRequest({
        shipperCode: head.shipperCode,
        returnInvoiceNumber: head.returnInvoiceNumber,
        originalInvoiceNumber: head.originalInvoiceNumber,
        receiverAddress: head.receiverAddress,
        receiverPhone: head.receiverPhone,
        receiverName: head.receiverName,
        receiptStartedAt: toYyyyMmDdHHmmss(receiptStartedAt),
        jobCommentForInvoice: head.jobCommentForInvoice,
        isSendToDoosonERP: head.isSendToDoosonERP,
        rows,
      })

      const validation = validateSaveRequest(request)
      if (!validation.success) {
        const validationMessage = validation.message ?? '저장 정보를 확인해 주세요.'
        if (validation.isRowIssue) scanNotice.rowInvalid(validationMessage)
        else scanNotice.invalid(validationMessage)
        return
      }

      isSavingRef.current = true
      setIsSaving(true)
      try {
        const result = await save(request)

        // 스캔 토스트는 한 장이라(같은 key) 안내를 차례로 띄우면 뒤 것이 앞을 덮는다 — 실패를 모아 한 장으로 끝내고,
        // 실패가 하나라도 있으면 '저장 완료'를 띄우지 않는다(반품작업 rgr06 saveSuccessWithDuplicates 와 같은 방식, #899 리뷰).
        const failures: string[] = []
        const duplicateOrderNumbers = new Set(result.duplicates.map((d) => d.orderNumber))
        const duplicateRows = rows.filter((row) => duplicateOrderNumbers.has(row.ORD_NO))
        if (duplicateRows.length > 0) failures.push(MESSAGES.saveDuplicates(duplicateRows.length))

        const attachmentFailure = await uploadAfterSave(head.shipperCode, attachmentTasks, result.attachmentTargetIds)
        if (attachmentFailure) failures.push(attachmentFailure)

        if (labelPrintData) {
          // 실패를 삼킨다(8.1-10) — qz-tray 미설치 환경에서도 저장 흐름은 끝까지 진행돼야 한다.
          await printHtml({ ...LABEL_PRINTER_CONFIG, html: buildLabelHtml(labelPrintData) }).catch(() => undefined)
        }

        if (failures.length > 0) scanNotice.failure(failures.join('\n'))
        else scanNotice.success(MESSAGES.saveSuccess)
        onSuccess({ duplicateRows, attachmentFailed: attachmentFailure != null })
      } catch (error) {
        // 403(타 화주)·500 등 저장 자체가 실패해도 안내 없이 넘어가지 않는다(PR #691 리뷰 코멘트, Critical).
        scanNotice.failure(apiErrorText(error, MESSAGES.saveFailed))
      } finally {
        isSavingRef.current = false
        setIsSaving(false)
      }
    },
    [save, uploadAfterSave],
  )

  return {
    runSave,
    isSaving: isSaving || saveMutation.isPending,
    /** 첨부 업로드가 실패해 재시도가 남아 있으면 true — 호출부가 재시도 버튼을 그린다. */
    pendingAttachmentRetry: pendingAttachments.length > 0,
    retryAttachmentUpload,
  }
}
